import { useState } from 'react';
import { api } from '../../utils/api';
import { usePatchingStore, getPatchingPhase } from '../../stores/usePatchingStore';
import { useConfirmStore } from '../../store/confirmStore';
import { PatchPanelMatrix } from './PatchPanelMatrix';
import { Cable, X, ArrowUp, ArrowDown, CheckCheck, AlertTriangle, CheckCircle, XCircle, Activity, RotateCcw } from 'lucide-react';

export function PatchingWorkbench() {
  const store = usePatchingStore();
  const [cableType, setCableType] = useState('Cat6');
  const [cableColor, setCableColor] = useState('blue');
  const [cableStatus, setCableStatus] = useState('Live');
  const [loading, setLoading] = useState(false);
  const [commitLoading, setCommitLoading] = useState(false);
  const [shiftLoading, setShiftLoading] = useState(false);
  
  // Local Toast state
  const [toast, setToast] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({type, message});
    setTimeout(() => setToast(null), 5000);
  };

  // Empty state when patching mode is not active
  if (!store.isPatchingMode) {
    return (
      <div className="flex flex-col items-center justify-center h-48 gap-4 text-center px-4 bg-gray-900 border-t-2 border-gray-800 shadow-2xl relative z-40 transition-all duration-300">
        <Cable className="text-blue-500/60" size={36} />
        <div>
          <p className="text-slate-300 font-semibold text-sm mb-1">Патч-панель</p>
          <p className="text-slate-500 text-xs leading-relaxed">
            Выберите устройство в стойке, затем нажмите<br />
            «Коммутировать» для соединения портов
          </p>
        </div>
        <button
          onClick={() => store.setPatchingMode(true)}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition flex items-center gap-2"
        >
          <Cable size={14} />
          Открыть патч-панель
        </button>
      </div>
    );
  }

  // [D3] Commit All: переводит все Planned кабели в Live
  const handleCommitAll = async () => {
    const allCables = [...store.leftCables, ...store.rightCables];
    // Дедупликация по ID (один кабель может быть виден с обеих сторон)
    const uniqueIds = [...new Set(
      allCables.filter(c => c.status === 'Planned').map(c => c.id)
    )];
    if (uniqueIds.length === 0) {
      showToast('error', 'Нет кабелей со статусом "Planned" в текущем рабочем пространстве.');
      return;
    }
    setCommitLoading(true);
    try {
      const res = await api.patch('/api/cables/batch-status', {
        cable_ids: uniqueIds,
        new_status: 'Live'
      });
      store.triggerPatchingRefresh();
      showToast('success', `Утверждено: ${res.data.updated} кабелей переведено в статус Live`);
    } catch (e: any) {
      console.error(e);
      showToast('error', 'Ошибка: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCommitLoading(false);
    }
  };

  // [D4] Soft Validation + Force Override
  const handleConnect = async () => {
    if (store.selectedLeftPorts.length !== store.selectedRightPorts.length) {
      showToast('error', 'Несовпадение: количество выбранных портов слева и справа должно совпадать.');
      return;
    }
    if (store.selectedLeftPorts.length === 0) {
      showToast('error', 'Выберите хотя бы один порт с каждой стороны.');
      return;
    }
    if (store.leftEquipmentId === store.rightEquipmentId) {
      showToast('error', 'Невозможно скоммутировать устройство само на себя (Self-patching запрещен).');
      return;
    }

    setLoading(true);
    try {
      const connections = [];
      for (let i = 0; i < store.selectedLeftPorts.length; i++) {
        connections.push({
          port_a_id: store.selectedLeftPorts[i],
          port_b_id: store.selectedRightPorts[i],
          cable_type: cableType,
          color: cableColor,
          status: cableStatus,
          force_override: false
        });
      }

      await api.post('/api/cables/batch', connections);
      store.clearLeftSelection();
      store.clearRightSelection();
      store.triggerPatchingRefresh();
      showToast('success', `Успешно создано ${connections.length} соединений!`);
    } catch (e: any) {
      // [D4] Обрабатываем 422 CONNECTOR_MISMATCH — показываем диалог
      const detail = e.response?.data?.detail;
      if (
        e.response?.status === 422 &&
        typeof detail === 'object' &&
        detail?.code === 'CONNECTOR_MISMATCH' &&
        detail?.force_override_allowed
      ) {
        const confirmed = await useConfirmStore.getState().confirm({
          title: 'Несовместимость разъемов',
          message: `Предупреждение: ${detail.message}\n\nЛевая сторона: ${detail.port_a_connector}\nПравая сторона: ${detail.port_b_connector}\n\nИнженер несёт ответственность за корректность соединения.\nВсё равно создать соединение? (Force Override)`,
          confirmText: 'Всё равно создать',
          cancelText: 'Отмена'
        });
        if (confirmed) {
          // Повторяем запрос с force_override: true
          try {
            const overrideConnections = store.selectedLeftPorts.map((leftId, i) => ({
              port_a_id: leftId,
              port_b_id: store.selectedRightPorts[i],
              cable_type: cableType,
              color: cableColor,
              status: cableStatus,
              force_override: true
            }));
            await api.post('/api/cables/batch', overrideConnections);
            store.clearLeftSelection();
            store.clearRightSelection();
            store.triggerPatchingRefresh();
            showToast('success', `Создано ${overrideConnections.length} соединений (Force Override применён).`);
          } catch (e2: any) {
            showToast('error', 'Ошибка: ' + (e2.response?.data?.detail || e2.message));
          }
        }
      } else {
        const errMsg = typeof detail === 'string'
          ? detail
          : detail?.message || e.message;
        showToast('error', 'Ошибка соединения: ' + errMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // [D5] Bulk Shift: сдвигает выделенные кабели на delta портов
  const handleShift = async (delta: number) => {
    // Для сдвига нужно найти кабели, которые уже воткнуты в выделенные порты
    const leftPortSet = new Set(store.selectedLeftPorts);
    const rightPortSet = new Set(store.selectedRightPorts);

    const cablesToShift = [...store.leftCables, ...store.rightCables].filter(c => {
      return leftPortSet.has(c.port_a_id) || leftPortSet.has(c.port_b_id) ||
             rightPortSet.has(c.port_a_id) || rightPortSet.has(c.port_b_id);
    });
    const uniqueCableIds = [...new Set(cablesToShift.map(c => c.id))];

    if (uniqueCableIds.length === 0) {
      showToast('error', 'Выберите порты с уже подключёнными кабелями для сдвига.');
      return;
    }

    // Определяем на какой стороне сдвигать — там где больше выделено
    const side = leftPortSet.size >= rightPortSet.size ? 'left' : 'right';

    setShiftLoading(true);
    try {
      const res = await api.post('/api/cables/bulk-shift', {
        cable_ids: uniqueCableIds,
        equipment_side: side,
        delta,
        workspace: 'master'
      });
      store.triggerPatchingRefresh();
      const direction = delta > 0 ? 'вниз' : 'вверх';
      showToast('success', `Сдвинуто ${res.data.shifted} кабелей ${direction} на ${Math.abs(delta)} порт(а).`);
    } catch (e: any) {
      showToast('error', 'Ошибка сдвига: ' + (e.response?.data?.detail || e.message));
    } finally {
      setShiftLoading(false);
    }
  };

  const plannedCount = [...new Set(
    [...store.leftCables, ...store.rightCables]
      .filter(c => c.status === 'Planned')
      .map(c => c.id)
  )].length;

  const canConnect = store.selectedLeftPorts.length > 0 &&
    store.selectedLeftPorts.length === store.selectedRightPorts.length &&
    store.leftEquipmentId !== store.rightEquipmentId;

  const hasSelectedWithCables = (() => {
    const leftSet = new Set(store.selectedLeftPorts);
    const rightSet = new Set(store.selectedRightPorts);
    return [...store.leftCables, ...store.rightCables].some(c =>
      leftSet.has(c.port_a_id) || leftSet.has(c.port_b_id) ||
      rightSet.has(c.port_a_id) || rightSet.has(c.port_b_id)
    );
  })();

  const phase = getPatchingPhase(store);
  const phaseLabels = {
    idle: { text: '1/3 Выберите приборы', cls: 'bg-slate-800 text-slate-400 border-slate-700' },
    source_ready: { text: '2/3 Выберите приёмник', cls: 'bg-cyan-950/60 text-cyan-300 border-cyan-700/60' },
    destination_ready: { text: '2/3 Выберите источник', cls: 'bg-indigo-950/60 text-indigo-300 border-indigo-700/60' },
    endpoints_ready: { text: '3/3 Выберите порты', cls: 'bg-amber-950/60 text-amber-300 border-amber-700/60' },
    ports_matched: { text: 'Готово к коммутации', cls: 'bg-emerald-950/60 text-emerald-300 border-emerald-700/60' },
  }[phase];

  return (
    <div className="h-[50vh] shrink-0 bg-gray-900 border-t-2 border-blue-500 shadow-2xl flex flex-col w-full relative z-40 transition-all duration-300 ease-in-out">
      {/* Header */}
      <div className="shrink-0 min-h-[60px] bg-gray-800 border-b border-gray-700 px-6 py-3 flex justify-between items-center shadow-md z-10 relative">
        <div className="flex items-center gap-3">
          <Cable className="text-blue-500" size={24} />
          <h2 className="text-xl font-bold text-white tracking-wide">Patching Workbench</h2>
          <span className={`text-[10px] font-mono px-2 py-0.5 rounded border uppercase font-bold tracking-wider ${phaseLabels.cls}`}>
            {phaseLabels.text}
          </span>
          {plannedCount > 0 && (
            <span className="flex items-center gap-1 text-xs bg-orange-900/50 border border-orange-700 text-orange-300 px-2 py-0.5 rounded-full">
              <AlertTriangle size={12} />
              {plannedCount} Planned
            </span>
          )}
        </div>
        
        {/* Toast Notification Container */}
        {toast && (
          <div className={`absolute top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-lg shadow-xl text-sm font-medium animate-in fade-in slide-in-from-top-4 z-50 flex items-center gap-2 ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
          }`}>
            {toast.type === 'error' ? <XCircle size={16} className="shrink-0" /> : <CheckCircle size={16} className="shrink-0" />}
            <span>{toast.message}</span>
          </div>
        )}

        <div className="flex items-center gap-2">
          {/* [D3] Commit All */}
          {plannedCount > 0 && (
            <button
              onClick={handleCommitAll}
              disabled={commitLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-green-700 hover:bg-green-600 text-white text-sm font-medium rounded transition-colors"
              title={`Утвердить все ${plannedCount} Planned кабелей как Live`}
            >
              <CheckCheck size={16} />
              {commitLoading ? 'Утверждаю...' : `Commit All (${plannedCount})`}
            </button>
          )}
          {/* Reset All */}
          {(store.leftEquipmentId || store.rightEquipmentId || store.selectedLeftPorts.length > 0 || store.selectedRightPorts.length > 0) && (
            <button
              onClick={() => store.resetWorkbench()}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-600 text-slate-300 hover:text-white text-xs font-medium rounded transition-colors"
              title="Очистить выбранные устройства и порты"
            >
              <RotateCcw size={14} />
              Сброс
            </button>
          )}
          <button
            onClick={() => store.setPatchingMode(false)}
            className="text-gray-400 hover:text-white bg-gray-800 p-2 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Main Split View */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Side */}
        <div className="flex-1 p-6 overflow-auto bg-gradient-to-r from-gray-900 to-gray-800 border-r border-gray-700">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-blue-400">Source Device</h3>
            {store.leftEquipmentId && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); store.setLeftEquipment(null); }}
                className="text-xs bg-red-900/50 hover:bg-red-800/60 text-red-300 px-2 py-1 rounded cursor-pointer transition-colors"
              >
                Clear
              </button>
            )}
          </div>
          {store.leftEquipmentId ? (
            <PatchPanelMatrix equipmentId={store.leftEquipmentId} isLeftPanel={true} />
          ) : (
            <div className="h-48 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-gray-500">
              Select an equipment from the rack to load into Source
            </div>
          )}
        </div>

        {/* Center Control Panel */}
        <div className="w-72 bg-gray-800 p-4 flex flex-col justify-center items-center border-x border-gray-700 shadow-xl z-10 gap-4">
          {/* Счётчик */}
          <div className="text-center">
            <p className="text-gray-400 text-sm mb-1">Соединений:</p>
            <p className="text-3xl font-bold text-white">
              {Math.min(store.selectedLeftPorts.length, store.selectedRightPorts.length)}
            </p>
          </div>

          {/* Параметры кабеля */}
          <div className="w-full space-y-3">
            <div>
              <label htmlFor="cable-type" className="text-xs text-gray-400 mb-1 block">Тип кабеля</label>
              <select
                id="cable-type"
                name="cableType"
                className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm"
                value={cableType}
                onChange={e => setCableType(e.target.value)}
              >
                <option value="Cat6">Cat6 (Copper)</option>
                <option value="Cat6a">Cat6a (Copper)</option>
                <option value="OM4">OM4 (Fiber)</option>
                <option value="OS2">OS2 (Fiber)</option>
                <option value="DAC">DAC (Twinax)</option>
              </select>
            </div>
            <div>
              <label htmlFor="cable-color" className="text-xs text-gray-400 mb-1 block">Цвет</label>
              <select
                id="cable-color"
                name="cableColor"
                className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm"
                value={cableColor}
                onChange={e => setCableColor(e.target.value)}
              >
                <option value="blue">Blue (Prod/LAN)</option>
                <option value="red">Red (Mgmt/OOB)</option>
                <option value="yellow">Yellow (Fiber)</option>
                <option value="green">Green (Storage)</option>
                <option value="purple">Purple</option>
              </select>
            </div>
            <div>
              <label htmlFor="cable-status" className="text-xs text-gray-400 mb-1 block">Статус</label>
              <select
                id="cable-status"
                name="cableStatus"
                className="w-full bg-gray-800 border border-gray-600 rounded p-2 text-white text-sm"
                value={cableStatus}
                onChange={e => setCableStatus(e.target.value)}
              >
                <option value="Live">Live (Connected)</option>
                <option value="Planned">Planned (To be connected)</option>
                <option value="Faulted">Faulted (Issue)</option>
              </select>
            </div>
          </div>

          {/* [D4] Auto-Patch с Soft Validation */}
          <button
            onClick={handleConnect}
            disabled={loading || !canConnect}
            className={`
              w-full py-3 rounded-lg font-bold text-sm shadow-lg transition-all flex items-center justify-center gap-2
              ${canConnect
                ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-500/20'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'}
            `}
          >
            {loading ? (
              'Подключаю...'
            ) : (
              <>
                <Activity size={16} />
                <span>Auto-Patch</span>
              </>
            )}
          </button>

          {/* [D5] Shift & Swap */}
          <div className="w-full border-t border-gray-700 pt-3">
            <p className="text-xs text-gray-500 text-center mb-2">Сдвиг выделенных кабелей</p>
            <div className="flex gap-2 w-full">
              <button
                onClick={() => handleShift(-1)}
                disabled={shiftLoading || !hasSelectedWithCables}
                className={`
                  flex-1 flex items-center justify-center gap-1 py-2 rounded font-medium text-sm transition-all
                  ${hasSelectedWithCables && !shiftLoading
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-800 text-gray-600 cursor-not-allowed'}
                `}
                title="Сдвинуть выделенные кабели вверх на 1 порт"
              >
                <ArrowUp size={16} />
                Вверх
              </button>
              <button
                onClick={() => handleShift(+1)}
                disabled={shiftLoading || !hasSelectedWithCables}
                className={`
                  flex-1 flex items-center justify-center gap-1 py-2 rounded font-medium text-sm transition-all
                  ${hasSelectedWithCables && !shiftLoading
                    ? 'bg-gray-700 hover:bg-gray-600 text-white'
                    : 'bg-gray-800 text-gray-600 cursor-not-allowed'}
                `}
                title="Сдвинуть выделенные кабели вниз на 1 порт"
              >
                <ArrowDown size={16} />
                Вниз
              </button>
            </div>
            {shiftLoading && (
              <p className="text-xs text-blue-400 text-center mt-1 animate-pulse">Сдвигаю...</p>
            )}
          </div>
        </div>

        {/* Right Side */}
        <div className="flex-1 p-6 overflow-auto bg-gradient-to-l from-gray-900 to-gray-800 border-l border-gray-700">
          <div className="mb-4 flex justify-between items-center">
            <h3 className="text-xl font-bold text-green-400">Destination Device</h3>
            {store.rightEquipmentId && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); store.setRightEquipment(null); }}
                className="text-xs bg-red-900/50 hover:bg-red-800/60 text-red-300 px-2 py-1 rounded cursor-pointer transition-colors"
              >
                Clear
              </button>
            )}
          </div>
          {store.rightEquipmentId ? (
            <PatchPanelMatrix equipmentId={store.rightEquipmentId} isLeftPanel={false} />
          ) : (
            <div className="h-48 border-2 border-dashed border-gray-700 rounded-lg flex items-center justify-center text-gray-500">
              Select an equipment from the rack to load into Destination
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
